import { Action } from "redux";
import ActionTypes from "./action-types";
import api from "src/spintr/SpintrApi";
import { AxiosError, AxiosResponse } from "axios";


export interface ISetCurrentResourceAction extends Action {
    currentResource: Spintr.ISystemStatusResource;
    type: ActionTypes.SetCurrentResource;
}

export interface ISetSystemStatusUnreadCountAction extends Action {
    unreadCount: number;
    type: ActionTypes.SetUnreadCount;
}

export interface ISetShowOutOfSeasonAction extends Action {
    showOutOfSeason: boolean;
    type: ActionTypes.SetShowOutOfSeason;
}

export interface IFetchSystemStatusUnreadCountAction extends Action {
    payload: Promise<AxiosResponse<number>>;
    type: ActionTypes.FetchUnreadCount;
}

export interface IFetchSystemStatusUnreadCountPendingAction extends Action {
    payload: Promise<AxiosResponse<number>>;
    type: ActionTypes.FetchUnreadCountPending;
}

export interface IFetchSystemStatusUnreadCountRejectedAction extends Action {
    payload: AxiosError;
    type: ActionTypes.FetchUnreadCountRejected;
}

export interface IFetchSystemStatusUnreadCountFulfilledAction extends Action {
    payload: AxiosResponse<number>;
    type: ActionTypes.FetchUnreadCountFulfilled;
}

export interface IFetchSystemStatusOngoingCountAction extends Action {
    payload: Promise<AxiosResponse<number>>;
    type: ActionTypes.FetchOngoingCount;
}

export interface IFetchSystemStatusOngoingCountPendingAction extends Action {
    payload: Promise<AxiosResponse<number>>;
    type: ActionTypes.FetchOngoingCountPending;
}

export interface IFetchSystemStatusOngoingCountRejectedAction extends Action {
    payload: AxiosError;
    type: ActionTypes.FetchOngoingCountRejected;
}

export interface IFetchSystemStatusOngoingCountFulfilledAction extends Action {
    payload: AxiosResponse<number>;
    type: ActionTypes.FetchOngoingCountFulfilled;
}

export interface IAddPrioritySystemStatusAction extends Action {
    systemStatus: Spintr.ISocialPostBase;
    type: ActionTypes.AddPrioritySystemStatus;
}

export interface IFetchPrioritySystemStatusesAction extends Action {
    payload: Promise<AxiosResponse<Spintr.ISocialPostBase[]>>;
    type: ActionTypes.FetchPrioritySystemStatuses,
}

export interface IFetchPrioritySystemStatusesPendingAction extends Action {
    payload: Promise<AxiosResponse<Spintr.ISocialPostBase[]>>;
    type: ActionTypes.FetchPrioritySystemStatusesPending,
}

export interface IFetchPrioritySystemStatusesRejectedAction extends Action {
    payload: AxiosError;
    type: ActionTypes.FetchPrioritySystemStatusesRejected,
}

export interface IFetchPrioritySystemStatusesFulfilledAction extends Action {
    payload: AxiosResponse<Spintr.ISocialPostBase[]>;
    type: ActionTypes.FetchPrioritySystemStatusesFulfilled,
}

export interface IFetchSystemStatusSettingsAction extends Action {
    payload: Promise<AxiosResponse<Spintr.ISystemStatusSettings>>;
    type: ActionTypes.FetchSettings;
}

export interface IFetchSystemStatusSettingsPendingAction extends Action {
    payload: Promise<AxiosResponse<Spintr.ISystemStatusSettings>>;
    type: ActionTypes.FetchSettingsPending;
}

export interface IFetchSystemStatusSettingsRejectedAction extends Action {
    payload: AxiosError;
    type: ActionTypes.FetchSettingsRejected;
}

export interface IFetchSystemStatusSettingsFulfilledAction extends Action {
    payload: AxiosResponse<Spintr.ISystemStatusSettings>;
    type: ActionTypes.FetchSettingsFulfilled;
}

export interface IPostSystemStatusViewedAction extends Action {
    meta: number;
    payload: Promise<AxiosResponse>;
    type: ActionTypes.PostSystemStatusViewed,
}

export interface IPostSystemStatusViewedPendingAction extends Action {
    meta: number;
    payload: Promise<AxiosResponse>;
    type: ActionTypes.PostSystemStatusViewedPending,
}

export interface IPostSystemStatusViewedRejectedAction extends Action {
    meta: number;
    payload: AxiosError;
    type: ActionTypes.PostSystemStatusViewedRejected,
}

export interface IPostSystemStatusViewedFulfilledAction extends Action {
    meta: number;
    payload: AxiosResponse;
    type: ActionTypes.PostSystemStatusViewedFulfilled,
}

export type SetCurrentResourceHandler
    = (currentResource: Spintr.ISystemStatusResource) => ISetCurrentResourceAction;

export type AddPrioritySystemStatusHandler
    = (status: Spintr.ISocialPostBase) => IAddPrioritySystemStatusAction;

export type FetchPrioritySystemStatusesHandler
    = () => IFetchPrioritySystemStatusesAction;

export type PostSystemStatusViewedHandler
    = (id: number) => IPostSystemStatusViewedAction;

export type SetSystemStatusUnreadCountHandler
    = (unreadCount: number) => ISetSystemStatusUnreadCountAction;

export type SetShowOutOfSeasonHandler
    = (showOutOfSeason: boolean) => ISetShowOutOfSeasonAction;

export type FetchSystemStatusUnreadCountHandler
    = () => IFetchSystemStatusUnreadCountAction;

export type FetchSystemStatusOngoingCountHandler
    = () => IFetchSystemStatusOngoingCountAction;

export type FetchSystemStatusSettingsHandler
    = () => IFetchSystemStatusSettingsAction;

export const setCurrentResource: SetCurrentResourceHandler
    = (currentResource: Spintr.ISystemStatusResource) => ({
        currentResource,
        type: ActionTypes.SetCurrentResource
    });

export const setSystemStatusUnreadCount: SetSystemStatusUnreadCountHandler
    = (unreadCount: number) => ({
        unreadCount,
        type: ActionTypes.SetUnreadCount,
    });

export const setShowOutOfSeason: SetShowOutOfSeasonHandler
    = (showOutOfSeason: boolean) => ({
        showOutOfSeason,
        type: ActionTypes.SetShowOutOfSeason,
    });


export const addPrioritySystemStatus: AddPrioritySystemStatusHandler
    = (systemStatus: Spintr.ISocialPostBase) => ({
        systemStatus,
        type: ActionTypes.AddPrioritySystemStatus,
    });

export const fetchPrioritySystemStatuses: FetchPrioritySystemStatusesHandler
    = () => ({
        payload: api.get("/api/v1/systemstatuses/prioritized"),
        type: ActionTypes.FetchPrioritySystemStatuses,
    });

export const postSystemStatusViewed: PostSystemStatusViewedHandler
    = (id: number) => ({
        meta: id,
        payload: api.post(`/api/v1/systemstatuses/${id}/viewed`),
        type: ActionTypes.PostSystemStatusViewed,
    });

export const fetchSystemStatusUnreadCount: FetchSystemStatusUnreadCountHandler
    = () => ({
        payload: api.get("/api/v1/systemstatuses/unread"),
        type: ActionTypes.FetchUnreadCount,
    });

export const fetchSystemStatusOngoingCount: FetchSystemStatusOngoingCountHandler
    = () => ({
        payload: api.get("/api/v1/systemstatuses/ongoing-count"),
        type: ActionTypes.FetchOngoingCount,
    });

export const fetchSystemStatusSettings: FetchSystemStatusSettingsHandler
    = () => ({
        payload: api.get("/api/v1/systemstatuses/settings"),
        type: ActionTypes.FetchSettings,
    });

export type SystemStatusAction
    = ISetCurrentResourceAction
    | IFetchSystemStatusOngoingCountFulfilledAction
    | IAddPrioritySystemStatusAction
    | IFetchPrioritySystemStatusesAction
    | IFetchPrioritySystemStatusesPendingAction
    | IFetchPrioritySystemStatusesRejectedAction
    | IFetchPrioritySystemStatusesFulfilledAction
    | IFetchSystemStatusSettingsAction
    | IFetchSystemStatusSettingsPendingAction
    | IFetchSystemStatusSettingsRejectedAction
    | IFetchSystemStatusSettingsFulfilledAction
    | ISetShowOutOfSeasonAction;

export default SystemStatusAction;
